/** One ServiceTitan customer a caller's number may belong to. */
export type CallerMatch = {
	id: number
	name: string | null
	address: string | null
	/** Present (false) only when ServiceTitan marks the customer inactive. The
	 * server drops such rows before they reach a screen (the owner's rule —
	 * see `visibleMatches`); the field stays so any reader that still receives
	 * one can apply the same rule. */
	active?: boolean
	/** Present only when the customer was NOT found by ServiceTitan's phone
	 * lookup itself: 'local_phone' (the lake's copy of their own contact),
	 * 'local_location' (their job site's contact — shown as "matched by the
	 * job site's number"), 'previous_call' (ServiceTitan attributed them to an
	 * earlier call from this number — "matched on a previous call"). */
	matched_on?: string
	/** With 'local_location': the job site whose number this is (its address
	 * is `address`), so the header can link to it. */
	st_location_id?: number
}

/**
 * The number is OURS (services/calls/staff.server.ts): a 3CX user's mobile or
 * direct line, a technician / employee from the lake's copy of ServiceTitan's
 * rosters — or, role `company`, one of the company's own numbers (a 3CX line
 * nobody in particular owns, or COMPANY_PHONE_NUMBERS: the main number Dialpad
 * presents as the caller on the calls it forwards to people's direct lines).
 * Such a caller is never offered as a possible customer — `matches` is empty
 * beside this — and is shown as "Staff: Mitchell McKee (mobile)" /
 * "Technician: Pat Tech" / "Forwarded from Dialpad — caller unknown" (or
 * "Transferred by the AI phone agent / by Retell — caller unknown", see
 * CompanyVia) (issues #162, #188).
 */
export type CallerStaff = {
	name: string
	role: 'user' | 'technician' | 'employee' | 'company'
	source: '3cx_mobile' | '3cx_did' | 'st_technician' | 'st_employee' | 'config'
	/** A configured company number another system put in the caller id
	 * (COMPANY_PHONE_TRANSFERS); absent = Dialpad, as it always was. */
	via?: CompanyVia
}

/**
 * What put a company number in the caller id. Dialpad — the main line and
 * every Dialpad department or call center — forwards calls with the real
 * caller id replaced; the AI phone agent (on Telnyx, not the 3CX trunk) and
 * Retell TRANSFER calls into 3CX with their own number as the caller. Which
 * number is which is configuration (COMPANY_PHONE_TRANSFERS in fly.toml,
 * beside COMPANY_PHONE_NUMBERS); these are only the words for each.
 */
export type CompanyVia = 'dialpad' | 'ai_agent' | 'retell'
export const COMPANY_VIAS: ReadonlyArray<CompanyVia> = [
	'dialpad',
	'ai_agent',
	'retell',
]
const COMPANY_VIA_WORDS: Record<
	CompanyVia,
	{ inbound: string; replaced: string; ours: string }
> = {
	dialpad: {
		inbound: 'Forwarded from Dialpad',
		replaced: 'Dialpad replaced the real one when it forwarded the call',
		ours: 'Dialpad department',
	},
	ai_agent: {
		inbound: 'Transferred by the AI phone agent',
		replaced:
			'the AI phone agent replaced the real one when it transferred the call',
		ours: 'AI phone agent',
	},
	retell: {
		inbound: 'Transferred by Retell',
		replaced: 'Retell replaced the real one when it transferred the call',
		ours: 'Retell',
	},
}

function companyWords(staff: CallerStaff) {
	return COMPANY_VIA_WORDS[staff.via ?? 'dialpad']
}

/**
 * The note under an INBOUND call from one of the company's own numbers: who
 * replaced the caller id, and what to do about it.
 */
export function companyCallerNote(staff: CallerStaff): string {
	return `The caller id is the company’s own number — ${companyWords(staff).replaced}. Ask the caller who they are.`
}

const STAFF_ROLES: ReadonlyArray<CallerStaff['role']> = [
	'user',
	'technician',
	'employee',
	'company',
]
const STAFF_SOURCES: ReadonlyArray<CallerStaff['source']> = [
	'3cx_mobile',
	'3cx_did',
	'st_technician',
	'st_employee',
	'config',
]

/**
 * A `staff` value read back off a call.matched event's JSON payload (the
 * readers in work/service.server.ts and calls/service.server.ts), or
 * undefined when it is not one: a hand-written event cannot put an arbitrary
 * label on a header.
 */
export function callerStaffOf(value: unknown): CallerStaff | undefined {
	if (typeof value !== 'object' || value === null || Array.isArray(value))
		return undefined
	const v = value as Record<string, unknown>
	if (
		typeof v.name !== 'string' ||
		!v.name.trim() ||
		!STAFF_ROLES.includes(v.role as CallerStaff['role']) ||
		!STAFF_SOURCES.includes(v.source as CallerStaff['source'])
	)
		return undefined
	return {
		name: v.name,
		role: v.role as CallerStaff['role'],
		source: v.source as CallerStaff['source'],
		// only a known system; anything else reads as it always did
		...(COMPANY_VIAS.includes(v.via as CompanyVia)
			? { via: v.via as CompanyVia }
			: {}),
	}
}

/**
 * The one line every screen uses for a staff caller — the header, the
 * /callerid rows, the texts board. `short` drops the "(mobile)" qualifier for
 * a tab label; `separator` replaces the ": " between the role and the name
 * (a callback task's "Caller: Staff — Sonya Baxter (direct line)").
 *
 * `direction`: for a call WE placed, a company number is not a forwarded
 * caller — somebody dialled one of HEP's own lines (a Dialpad department, or
 * a line the PBX routes). The staff lookup keys on the call's other party
 * whichever way it went, so the words have to follow the direction.
 */
export function staffLabel(
	staff: CallerStaff,
	opts: { short?: boolean; separator?: string; direction?: string | null } = {},
): string {
	if (staff.role === 'company' && opts.direction === 'outbound') {
		if (staff.source !== 'config') return 'HEP — company number'
		const ours = companyWords(staff).ours
		return opts.short ? `HEP — ${ours}` : `HEP — ${ours} (company number)`
	}
	if (staff.role === 'company') {
		const inbound = companyWords(staff).inbound
		return opts.short ? inbound : `${inbound} — caller unknown`
	}
	const who = staff.role === 'technician' ? 'Technician' : 'Staff'
	const via = opts.short
		? ''
		: staff.source === '3cx_mobile'
			? ' (mobile)'
			: staff.source === '3cx_did'
				? ' (direct line)'
				: ''
	return `${who}${opts.separator ?? ': '}${staff.name}${via}`
}

/** A phone match is a possible identity, never an automatic intake selection. */
export type CallerIdentity = {
	status: 'pending' | 'done' | 'failed'
	matches: Array<CallerMatch>
	/** Present when the number is one of our own people's (see CallerStaff):
	 * the lookup asked nobody for a customer, and `matches` is empty. */
	staff?: CallerStaff
	/** Present (true) only when ServiceTitan had MORE customers on this number
	 * than the lookup's limit returned — `matches` is then not the whole list. */
	has_more?: boolean
	/** Present (true) once this call's booking session holds a confirmed
	 * customer. That confirmation always wins: the header choice is read-only
	 * and the server refuses it (409). */
	confirmed_in_booking?: boolean
}

/**
 * The owner's rule: an inactive ServiceTitan customer is never shown — not as
 * the possible customer, not in the pick-list, not on a call row, not as a
 * booking quick-pick. A lookup whose only matches are inactive reads exactly
 * like no match ("Unmatched number"). The call's journal (the call.matched
 * event's `inactive_ids`) keeps the fact, so a CSR who asks can be told.
 */
export function visibleMatches<T extends { active?: boolean | null }>(
	matches: ReadonlyArray<T>,
): Array<T> {
	return matches.filter((m) => m.active !== false)
}
